import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download, FileUp, MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useWorkflowLifecycleStore } from '@/stores/workflow-lifecycle-store'
import { useWorkflowStore } from '@/stores/workflow-store'
import { workflowLifecycleService } from '@/services/workflow-lifecycle'
import { downloadWorkflowExport } from '@/lib/workflows/export-import'
import { validateGraph } from '@/components/workflows/flow/validation'
import { cn } from '@/lib/utils'
import type { Workflow } from '@/schemas/workflow'
import { ImportWorkflowDialog } from './import-workflow-dialog'

interface PublishControlsProps {
  projectId: string
  workflowId: string
  workflow: Workflow
  onPublished?: () => void
}

const STATUS_CHIP_CLASS: Record<string, string> = {
  draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  inactive: 'bg-red-500/20 text-red-300 border-red-500/30',
}

export function PublishControls({
  projectId,
  workflowId,
  workflow,
  onPublished,
}: PublishControlsProps) {
  const { t } = useTranslation('workflows')
  const [confirmUnpublish, setConfirmUnpublish] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [publishBusy, setPublishBusy] = useState(false)
  const [unpublishBusy, setUnpublishBusy] = useState(false)

  const publish = useWorkflowLifecycleStore((s) => s.publish)
  const unpublish = useWorkflowLifecycleStore((s) => s.unpublish)
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const flowEdges = useWorkflowStore((s) => s.flowEdges)

  const graphValid = useMemo(
    () => validateGraph(flowNodes, flowEdges).valid,
    [flowNodes, flowEdges]
  )

  const isPublished = workflow.status === 'active'
  const publishDisabled = isPublished || !graphValid || publishBusy
  const publishTooltip = !graphValid
    ? t('lifecycle.publish.validationBlocked')
    : null

  const handlePublish = async () => {
    setPublishBusy(true)
    const v = await publish(projectId, workflowId)
    setPublishBusy(false)
    if (v && 'version_number' in v) {
      toast.success(t('lifecycle.publish.success', { n: v.version_number }))
      onPublished?.()
    } else if (v && 'error' in v && v.error === 'nothing_to_publish') {
      toast.info(
        t('lifecycle.publish.noChanges', { n: workflow.version })
      )
    } else {
      toast.error(t('lifecycle.publish.validationBlocked'))
    }
  }

  const handleUnpublishConfirm = async () => {
    setUnpublishBusy(true)
    const ok = await unpublish(projectId, workflowId)
    setUnpublishBusy(false)
    setConfirmUnpublish(false)
    if (ok) toast.success(t('lifecycle.unpublish.success'))
    else toast.error(t('lifecycle.unpublish.failure'))
  }

  const handleExport = async () => {
    try {
      const data = await workflowLifecycleService.exportWorkflow(
        projectId,
        workflowId
      )
      downloadWorkflowExport(
        data,
        isPublished ? `v${workflow.version}` : 'draft'
      )
      toast.success(t('lifecycle.export.success'))
    } catch {
      toast.error(t('lifecycle.export.failure'))
    }
  }

  return (
    <div className="flex items-center gap-2" data-testid="publish-controls">
      <Badge
        className={cn(
          'border text-xs',
          STATUS_CHIP_CLASS[workflow.status] ?? STATUS_CHIP_CLASS.draft
        )}
        data-testid="publish-status-chip"
      >
        {isPublished
          ? t('status.active')
          : workflow.status === 'inactive'
            ? t('status.inactive')
            : t('status.draft')}
      </Badge>

      {!isPublished ? (
        publishTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper so tooltip still fires while button is disabled */}
              <span>
                <Button
                  id="publish-cta"
                  onClick={handlePublish}
                  disabled={publishDisabled}
                  variant="action-emerald"
                >
                  {t('lifecycle.publish.cta')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{publishTooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            id="publish-cta"
            onClick={handlePublish}
            disabled={publishDisabled}
            variant="action-emerald"
          >
            {t('lifecycle.publish.cta')}
          </Button>
        )
      ) : (
        <Button
          onClick={() => setConfirmUnpublish(true)}
          variant="outline"
          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
        >
          {t('lifecycle.unpublish.cta')}
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mais ações"
            data-testid="publish-controls-more"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            {t('lifecycle.export.cta')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setImportOpen(true)}>
            <FileUp className="mr-2 h-4 w-4" />
            {t('lifecycle.import.cta')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmationDialog
        open={confirmUnpublish}
        onOpenChange={setConfirmUnpublish}
        title={t('lifecycle.unpublish.confirmTitle')}
        description={t('lifecycle.unpublish.confirmBody')}
        confirmLabel={t('lifecycle.unpublish.confirmCta')}
        confirmingLabel={t('lifecycle.unpublish.confirmCta')}
        confirmVariant="action-red"
        isProcessing={unpublishBusy}
        onConfirm={handleUnpublishConfirm}
      />

      <ImportWorkflowDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={projectId}
      />
    </div>
  )
}
